/**
 * Session content client (contract v1): `GET /api/web/config/:pk/session?locale=&pageTitle=&pagePath=`. The answer
 * REPLACES the public slots for this visitor. Never cached, never persisted. Every failure is
 * soft: the widget keeps the public projection (404 until BE 074 ships, 5xx, network, timeout).
 */
import { apiBase } from "../api";
import {
  PREVIEW_TOKEN_HEADER,
  type PreviewReason,
  previewReasonFromBody,
} from "../preview/preview-codes";
import type { SessionPageParams } from "../visitor-page/session-page-params";
import type { ContentSlots } from "../widget-config";
import { mapCapabilities, mapContentSlots } from "./map-content";

/** BE target p95 < 300 ms; no p99 guarantee, so a bounded wait then the public projection. */
export const SESSION_CONTENT_TIMEOUT_MS = 3_000;

export type SessionContentResult =
  | {
      kind: "ok";
      visitors: "ANONYMOUS" | "SIGNED_IN" | null;
      /** The contact is blocked (`blocked` on the answer, which then carries no slots). `null` = the
       *  field is absent (an older BE): unknown, not "not blocked". */
      blocked: boolean | null;
      slots: ContentSlots;
      /** BE feature flags of this answer; `null` = the field is absent (keep the public config's). */
      capabilities: string[] | null;
    }
  /** The token was rejected: whatever session content we held no longer applies. */
  | { kind: "unauthorized"; previewReason?: PreviewReason }
  | { kind: "failed" };

export async function fetchSessionContent(args: {
  publicKey: string;
  token: string;
  /** BE preview token (BE 062): rides in a header, like on every other visitor call. */
  previewToken?: string | null;
  locale?: string;
  /** The host page the session starts on: the BE renders `page.*` variables from it. */
  page?: SessionPageParams;
  signal?: AbortSignal;
  timeoutMs?: number;
}): Promise<SessionContentResult> {
  const controller = new AbortController();
  const onCallerAbort = () => controller.abort();
  args.signal?.addEventListener("abort", onCallerAbort);
  if (args.signal?.aborted) controller.abort();
  const timer = setTimeout(() => controller.abort(), args.timeoutMs ?? SESSION_CONTENT_TIMEOUT_MS);
  try {
    const url = new URL(
      `${apiBase()}/api/web/config/${encodeURIComponent(args.publicKey)}/session`,
    );
    if (args.locale) url.searchParams.set("locale", args.locale);
    if (args.page?.pageTitle) url.searchParams.set("pageTitle", args.page.pageTitle);
    if (args.page?.pagePath) url.searchParams.set("pagePath", args.page.pagePath);
    const res = await fetch(url.toString(), {
      method: "GET",
      headers: {
        "x-web-public-key": args.publicKey,
        Authorization: `Bearer ${args.token}`,
        ...(args.previewToken ? { [PREVIEW_TOKEN_HEADER]: args.previewToken } : {}),
      },
      credentials: "omit",
      cache: "no-store",
      signal: controller.signal,
    });
    if (args.previewToken && (res.status === 401 || res.status === 403)) {
      const previewReason = previewReasonFromBody(await res.json().catch(() => null));
      if (previewReason) return { kind: "unauthorized", previewReason };
    }
    if (res.status === 401) return { kind: "unauthorized" };
    if (!res.ok) return { kind: "failed" };
    const body: unknown = await res.json();
    if (!body || typeof body !== "object" || Array.isArray(body)) return { kind: "failed" };
    const v = (body as { visitors?: unknown }).visitors;
    return {
      kind: "ok",
      visitors: v === "ANONYMOUS" || v === "SIGNED_IN" ? v : null,
      blocked:
        typeof (body as { blocked?: unknown }).blocked === "boolean"
          ? (body as { blocked: boolean }).blocked
          : null,
      slots: mapContentSlots(body),
      capabilities: Array.isArray((body as { capabilities?: unknown }).capabilities)
        ? mapCapabilities((body as { capabilities: unknown }).capabilities)
        : null,
    };
  } catch {
    return { kind: "failed" };
  } finally {
    clearTimeout(timer);
    args.signal?.removeEventListener("abort", onCallerAbort);
  }
}
